/* eslint-disable react-hooks/exhaustive-deps */
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import type { AgentType } from '../types/chat';

/**
 * Environment scope mappings for Ellis DNT Chat
 * Maps each environment (dev, uat, prod) and tool (jira-agile, ado-agile, knowledge-graph)
 * to their respective scope IDs
 */
export const envScopesMapping = {
  dev: {
    'jira-agile': {
      domainId: 'ellischat_d4c16a7985334f65969e768769dd631b',
      subDomainId: 'Jira_b264fdbd977243dab2ad96b49e9ab64f',
      personaId: 'Jira_5c3342652b324e9b9255fc75ad6b2ca7',
    },
    'ado-agile': {
      domainId: 'ellischat_d4c16a7985334f65969e768769dd631b',
      subDomainId: 'ADO_0badd053cf7e491ea81aed35b243cd21',
      personaId: 'ADO_e5b6cefd717e4606b992992f2db84aac',
    },
    'knowledge-graph': {
      domainId: 'ellischat_d4c16a7985334f65969e768769dd631b',
      subDomainId: 'KnowledgeBase_bd3d81fa6ade41cfb1077366273f2120',
      personaId: 'KnowledgeBase_5230939f6f514d97bcdd10d4be24b87c',
    },
  },
  uat: {
    'jira-agile': {
      domainId: 'ellischat_d346cb64e98e4606861ef1ef3ae5d221',
      subDomainId: 'Jira_3d3ad7d9044a4d86b935a333c13a4e17',
      personaId: 'Jira_5c90c474177643d2a8825d749f887fd0',
    },
    'ado-agile': {
      domainId: 'ellischat_d346cb64e98e4606861ef1ef3ae5d221',
      subDomainId: 'ADO_48ef4aa1c5f344dba51a3721591347d3',
      personaId: 'ADO_542524871395421a888416d6fe380ea7',
    },
    'knowledge-graph': {
      domainId: 'ellischat_d346cb64e98e4606861ef1ef3ae5d221',
      subDomainId: 'KnowledgeBase_6dd021d3275f42c89aa5ef4776aba4e4',
      personaId: 'KnowledgeBase_2c3ec62c391c420f9e6b4eab4aaef4e5',
    },
  },
  prod: {
    'jira-agile': {
      domainId: 'ellischat_8ca8ec25865c48a2b79b34ba48795d5c',
      subDomainId: 'Jira_4cc5fb9d8ebc4ef3a93a4bd292266820',
      personaId: 'Jira_df41c4f06cfd4964a8a1eb6b39e3d639',
    },
    'ado-agile': {
      domainId: 'ellischat_8ca8ec25865c48a2b79b34ba48795d5c',
      subDomainId: 'ADO_8d83631124024bbabeb89fd3f57f077c',
      personaId: 'ADO_7591a0f19d694e8795b5d88a8c184425',
    },
    'knowledge-graph': {
      domainId: 'ellischat_8ca8ec25865c48a2b79b34ba48795d5c',
      subDomainId: 'KnowledgeBase_c52420e7916f44d284a560dc71f760a3',
      personaId: 'KnowledgeBase_1aed8a69544d4922b70b29ed1916083a',
    },
  },
};

export interface EnvScopeResult {
  domainId: string;
  subDomainId: string;
  personaId: string;
  env: string;
  tool: AgentType;
}

/**
 * Hook to get environment-specific and tool-specific scope IDs
 * Similar to useEnvScopes from PromptStore but handles multiple tools
 *
 * @param currentTool - The currently selected tool/agent (jira-agile, ado-agile, knowledge-graph)
 * @returns Scopes for domainId, subDomainId, personaId along with current env and tool
 */
export const useEnvScopes = (
  currentTool: AgentType = 'jira-agile',
): EnvScopeResult => {
  const configApi = useApi(configApiRef);

  let env = configApi.getString('app.datadogRum.env') as
    | 'internal'
    | 'dev'
    | 'uat'
    | 'prod';

  // For local testing, default to dev if env is not recognized
  const envArray = ['dev', 'uat', 'prod'];
  if (!envArray.includes(env)) {
    env = 'dev';
  }

  // Ensure tool is one of the three supported tools
  const supportedTools = ['jira-agile', 'ado-agile', 'knowledge-graph'];
  const tool = supportedTools.includes(currentTool)
    ? (currentTool as AgentType)
    : 'jira-agile';

  // Get scopes for the current environment and tool
  const scopes = envScopesMapping[env as keyof typeof envScopesMapping]?.[
    tool as keyof (typeof envScopesMapping)[keyof typeof envScopesMapping]
  ] || {
    domainId: '',
    subDomainId: '',
    personaId: '',
  };

  return {
    ...scopes,
    env,
    tool,
  };
};
